import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { Alert, PageHeader } from './ui.tsx';
import {
  PLANS,
  RECOMMENDED_PLAN_ID,
  isPlanId,
  minimumPlanFor,
  planHasFeature,
  planRanksBelow,
  type PlanFeature,
} from '@shared/constants/index.ts';
import type { Company } from '@shared/types/index.ts';

/**
 * The client-side half of the plan gates. The Worker is the authority — every
 * gated route answers `403 PLAN_REQUIRED` on its own — and this only decides
 * what to render, so that a company on the Free plan meets an explanation
 * instead of a screen that loads and then errors.
 *
 * `effectivePlan` is computed server-side (see services/entitlements.ts) and
 * already accounts for a deployment with no billing configured, where nothing
 * is gated at all. A session that has not loaded yet is treated as entitled:
 * the alternative is flashing an upgrade notice at a paying customer for the
 * length of one query.
 */
export function usePlanFeature(feature: PlanFeature): boolean {
  return companyHasPlanFeature(useSession().company, feature);
}

/**
 * The same question where there is no hook to call — a nav filter, a route
 * redirect. A company whose `effectivePlan` is missing or unrecognized counts
 * as entitled for the same reason a loading one does: the Worker refuses what
 * it must, and guessing "no" here would hide the app from somebody paying for
 * it because one field arrived malformed.
 */
export function companyHasPlanFeature(company: Company | null, feature: PlanFeature): boolean {
  if (company === null || !isPlanId(company.effectivePlan)) return true;
  return planHasFeature(company.effectivePlan, feature);
}

/**
 * What a page says instead of itself when the plan does not carry it: what
 * the feature is, the cheapest plan that includes it, and — for the one
 * person who can act on that — the way to Billing.
 */
export function PlanRequiredNotice({ feature, children }: { feature: PlanFeature; children?: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useSession();
  const minimum = minimumPlanFor(feature);
  const planName = PLANS[minimum].name;
  // The cheapest plan that opens this page is always named first — nobody
  // should be sold more than they came for. Where that plan is below Pro,
  // one line follows saying what Pro adds, because the company standing at
  // this gate is usually about to weigh the two.
  const suggestsRecommended = planRanksBelow(minimum, RECOMMENDED_PLAN_ID);
  const recommendedName = PLANS[RECOMMENDED_PLAN_ID].name;

  return (
    <Alert tone="info" title={t(`planGate.${feature}.title`)}>
      <p>{t(`planGate.${feature}.body`, { plan: planName })}</p>
      {suggestsRecommended ? <p className="mt-2">{t('planGate.recommended', { plan: recommendedName })}</p> : null}
      {children}
      <p className="mt-2">
        {user?.role === 'owner' ? (
          <Link to="/settings#settings-billing" className="font-semibold text-[color:var(--color-brand)] underline">
            {t('planGate.upgradeCta', { plan: planName })}
          </Link>
        ) : (
          t('planGate.askOwner', { plan: planName })
        )}
      </p>
    </Alert>
  );
}

/**
 * A whole page the plan does not carry: its usual header, so the person knows
 * they arrived where they meant to, and the notice in place of the content.
 * Pages reach for this rather than redirecting, because a redirect answers
 * "where did Scheduling go?" with a shrug.
 */
export function PlanRequiredPage({ feature, title }: { feature: PlanFeature; title: string }) {
  return (
    <>
      <PageHeader title={title} />
      <PlanRequiredNotice feature={feature} />
    </>
  );
}
